import { Injectable } from '@angular/core';

import {
  SyncExporter,
  ExporterDescriptor,
  ExportArtifact
} from '../models/exporter';
import {
  ExportPayload,
  ExportTaskRecord
} from '../models/export-payload';
import { ExportProvenance } from '../models/provenance';
import { EXTRA_COLUMNS_CSV_XLSX, ExtraColumnSpec } from './extras-utils';

/**
 * Exporter XLSX (síncrono, 100% en cliente).
 *
 * Estándar 2026:
 *   - Librería ExcelJS (MIT). Mejor API de estilos, sin drama de licencias.
 *   - Carga LAZY vía dynamic import: el bundle principal de la app no se
 *     infla; el chunk se descarga la primera vez que un usuario invoca
 *     este exporter. Webpack/Vite hacen el code split automáticamente.
 *   - Dos hojas: "Tareas" (mismas 12 columnas que el CSV, con estilado)
 *     y "Resumen" (totales por perfil/sistema + conteo origen + metadata).
 *
 * Esquema visual (traslación a Excel de tu semántica Diff):
 *   - Tint de fila base = origen
 *       IA       → verde pálido   (deriva de $accent-neon   #a6e22e)
 *       Histórica→ cyan pálido    (deriva de $accent-cyan   #66d9ef)
 *       Manual   → naranja pálido (deriva de $accent-orange #fd971f)
 *   - Barra amarilla 3px en borde izquierdo = (Modificada)
 *       (eco de $accent-yellow #ffbd2e)
 *
 * Lectura natural: el color te dice DE DÓNDE viene la tarea, la barra
 * amarilla te dice QUIÉN LA TOCÓ.
 */
@Injectable({ providedIn: 'root' })
export class XlsxExporter implements SyncExporter {

  readonly descriptor: ExporterDescriptor & { mode: 'sync' } = {
    id: 'xlsx',
    label: 'Excel (XLSX)',
    shortHint: 'Hoja · estilado por origen',
    iconSvgPath:
      'M19 3H5c-1.11 0-2 .89-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm-3.86 14.94L12 13.93l-3.14 4.01L7.4 16.5l3.32-4.5-3.32-4.5 1.46-1.44L12 10.07l3.14-4.01 1.46 1.44-3.32 4.5 3.32 4.5-1.46 1.44z',
    mode: 'sync',
    requiredPermission: 'estimation:export',
    supportsExtras: true,
    order: 20
  };

  /** Definición declarativa de columnas — alineada 1:1 con el CSV. */
  private readonly columns: Array<{
    header: string;
    key: string;
    width: number;
    wrap?: boolean;
    numFmt?: string;
    pick: (t: ExportTaskRecord) => unknown;
  }> = [
    { header: 'id',         key: 'id',         width: 12, pick: t => t.id },
    { header: 'bloque',     key: 'bloque',     width: 38, wrap: true,  pick: t => t.requirementDescription },
    { header: 'tarea',      key: 'tarea',      width: 60, wrap: true,  pick: t => t.description },
    { header: 'tipo',       key: 'tipo',       width: 14, pick: t => t.type },
    { header: 'subsistema', key: 'subsistema', width: 20, pick: t => t.subsystem },
    { header: 'stack',      key: 'stack',      width: 16, pick: t => t.technology },
    { header: 'perfil',     key: 'perfil',     width: 22, pick: t => t.profile },
    { header: 'horas',      key: 'horas',      width: 8,  numFmt: '0',   pick: t => t.hours },
    { header: 'p10',        key: 'p10',        width: 8,  numFmt: '0',   pick: t => t.p10Hours ?? null },
    { header: 'p90',        key: 'p90',        width: 8,  numFmt: '0',   pick: t => t.p90Hours ?? null },
    { header: 'confianza',  key: 'confianza',  width: 12, pick: t => t.confidence ?? '' },
    { header: 'origen',     key: 'origen',     width: 22, pick: t => t.provenanceLabel }
  ];

  // Paleta interna alineada con variables.scss (canal alpha en hex).
  // Excel usa ARGB; los tints van con alpha ~22 (10-15% intensidad).
  private readonly STYLE = {
    headerFill:     'FF1E1E1E',  // $bg-surface
    headerFont:     'FFA6E22E',  // $accent-neon
    borderSubtle:   'FF333333',  // $border-color
    rowTintAi:      '22A6E22E',  // verde neón al ~13% (transparencia visual sobre blanco)
    rowTintHist:    '2266D9EF',  // cyan
    rowTintManual:  '22FD971F',  // naranja
    rowTintUnknown: '00000000',  // sin tinte
    barModified:    'FFFFBD2E',  // amarillo Modificada (border-left)
    sectionFill:    'FFF5F5F5',  // gris claro para tablas Resumen
    sectionFont:    'FF1E1E1E'
  };

  async generate(payload: ExportPayload, _options: unknown): Promise<ExportArtifact> {
    // Dynamic import: el chunk de ExcelJS solo entra cuando se usa.
    const ExcelJS = await import('exceljs');
    const Workbook: any = (ExcelJS as any).Workbook ?? (ExcelJS as any).default?.Workbook;
    const wb = new Workbook();

    wb.creator = payload.estimation.exportedBy.name;
    wb.created = new Date(payload.estimation.exportedAt);

    this.buildTasksSheet(wb, payload);
    this.buildSummarySheet(wb, payload);

    const buffer: ArrayBuffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    return {
      filename: this.buildFilename(payload),
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      blob
    };
  }

  // ==========================================================================
  // HOJA "TAREAS"
  // ==========================================================================

  private buildTasksSheet(wb: any, payload: ExportPayload): void {
    const ws = wb.addWorksheet('Tareas', {
      views: [{ state: 'frozen', ySplit: 1 }]   // freeze de la cabecera
    });

    // Si el payload viene enriquecido (toggle "completo"), añadimos las
    // columnas extras del helper centralizado al final. DRY total — el
    // mismo EXTRA_COLUMNS_CSV_XLSX se usa también en csv.exporter.ts.
    const effectiveColumns = payload.extrasIncluded
      ? [...this.columns, ...EXTRA_COLUMNS_CSV_XLSX.map(adaptExtraToXlsxColumn)]
      : this.columns;

    // Columnas declarativas
    ws.columns = effectiveColumns.map(c => ({
      header: c.header,
      key: c.key,
      width: c.width
    }));

    // Estilado de la cabecera (oscuro + neón, eco del cyber-table)
    const headerRow = ws.getRow(1);
    headerRow.height = 22;
    headerRow.eachCell((cell: any) => {
      cell.font = { bold: true, color: { argb: this.STYLE.headerFont }, name: 'Consolas', size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.STYLE.headerFill } };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.border = {
        bottom: { style: 'thin', color: { argb: this.STYLE.headerFont } }
      };
    });

    // Filas de datos
    payload.tasks.forEach(task => {
      const row = ws.addRow(
        effectiveColumns.reduce((acc: Record<string, unknown>, c) => {
          acc[c.key] = c.pick(task);
          return acc;
        }, {})
      );
      this.styleTaskRow(row, task);
    });

    // Format numérico + wrap por columna (también para las extras).
    effectiveColumns.forEach((c, i) => {
      const col = ws.getColumn(i + 1);
      // Una columna `wrap` puede coexistir con `numFmt` solo si numFmt está set.
      if ((c as any).numFmt) col.numFmt = (c as any).numFmt;
      else if ((c as ExtraColumnSpec).isNumeric) col.numFmt = '0.00';
      if (c.wrap) col.alignment = { wrapText: true, vertical: 'top' };
    });

    // Autofilter sobre todo el rango con datos
    const lastCol = effectiveColumns.length;
    const lastRow = payload.tasks.length + 1;
    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to:   { row: 1, column: lastCol }
    };

    // (No añadimos fila TOTAL en esta hoja — los totales viven en "Resumen".
    //  Esto permite que la hoja "Tareas" sea re-importable a otras herramientas.)
    void lastRow;
  }

  /** Aplica tint de fila por origen + barra amarilla si está modificada. */
  private styleTaskRow(row: any, task: ExportTaskRecord): void {
    const tint = this.tintForProvenance(task.provenance);
    const isModified = this.isModifiedProvenance(task.provenance);

    row.eachCell({ includeEmpty: false }, (cell: any, colNumber: number) => {
      cell.alignment = cell.alignment || { vertical: 'top' };
      cell.font = cell.font || { name: 'Calibri', size: 11 };

      if (tint && tint !== '00000000') {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tint } };
      }

      // Border-bottom sutil para separar filas (eco del cyber-table).
      cell.border = {
        ...(cell.border ?? {}),
        bottom: { style: 'hair', color: { argb: this.STYLE.borderSubtle } }
      };

      // Barra amarilla 3px en la PRIMERA columna si la tarea es Modificada.
      if (isModified && colNumber === 1) {
        cell.border = {
          ...cell.border,
          left: { style: 'thick', color: { argb: this.STYLE.barModified } }
        };
      }
    });

    row.height = 18;
  }

  private tintForProvenance(p: ExportProvenance): string {
    switch (p) {
      case ExportProvenance.AI:
      case ExportProvenance.AI_MODIFIED:
        return this.STYLE.rowTintAi;
      case ExportProvenance.HISTORIC:
      case ExportProvenance.HISTORIC_MODIFIED:
        return this.STYLE.rowTintHist;
      case ExportProvenance.MANUAL:
      case ExportProvenance.MANUAL_MODIFIED:
        return this.STYLE.rowTintManual;
      default:
        return this.STYLE.rowTintUnknown;
    }
  }

  private isModifiedProvenance(p: ExportProvenance): boolean {
    return (
      p === ExportProvenance.AI_MODIFIED ||
      p === ExportProvenance.HISTORIC_MODIFIED ||
      p === ExportProvenance.MANUAL_MODIFIED
    );
  }

  // ==========================================================================
  // HOJA "RESUMEN"
  // ==========================================================================

  private buildSummarySheet(wb: any, payload: ExportPayload): void {
    const ws = wb.addWorksheet('Resumen');
    ws.columns = [
      { width: 32 },
      { width: 16 }
    ];

    let cursor = 1;

    // Cabecera global
    cursor = this.writeSummaryHeader(ws, cursor, 'TOTALES GENERALES');
    cursor = this.writeKV(ws, cursor, 'Total de tareas',  payload.summary.taskCount);
    cursor = this.writeKV(ws, cursor, 'Total de horas',   payload.summary.totalHours, '0');
    cursor += 1;

    // Totales por perfil
    cursor = this.writeSummaryHeader(ws, cursor, 'HORAS POR PERFIL');
    cursor = this.writeTwoColumnTable(
      ws, cursor,
      ['perfil', 'horas'],
      Object.entries(payload.summary.hoursByProfile)
        .sort((a, b) => b[1] - a[1]),
      '0'
    );
    cursor += 1;

    // Totales por subsistema
    cursor = this.writeSummaryHeader(ws, cursor, 'HORAS POR SUBSISTEMA');
    cursor = this.writeTwoColumnTable(
      ws, cursor,
      ['subsistema', 'horas'],
      Object.entries(payload.summary.hoursBySystem)
        .sort((a, b) => b[1] - a[1]),
      '0'
    );
    cursor += 1;

    // Conteo por origen
    cursor = this.writeSummaryHeader(ws, cursor, 'TAREAS POR ORIGEN');
    const provRows: Array<[string, number]> = (Object.keys(payload.summary.provenanceCounts) as ExportProvenance[])
      .map(p => [this.humanProvenance(p), payload.summary.provenanceCounts[p]] as [string, number])
      .filter(([, count]) => count > 0);
    cursor = this.writeTwoColumnTable(ws, cursor, ['origen', 'tareas'], provRows, '0');
    cursor += 2;

    // Metadata embebida al final (no rompe la limpieza visual del resumen)
    cursor = this.writeSummaryHeader(ws, cursor, 'METADATA');
    cursor = this.writeKV(ws, cursor, 'Estimación ID',     payload.estimation.id);
    if (payload.estimation.name)         cursor = this.writeKV(ws, cursor, 'Nombre',         payload.estimation.name);
    if (payload.estimation.generatedAt)  cursor = this.writeKV(ws, cursor, 'Creada',         payload.estimation.generatedAt);
    cursor = this.writeKV(ws, cursor, 'Exportada',         payload.estimation.exportedAt);
    cursor = this.writeKV(ws, cursor, 'Exportada por',     payload.estimation.exportedBy.name);
    cursor = this.writeKV(ws, cursor, 'Schema version',    payload.estimation.schemaVersion);
    if (payload.estimation.promptExcerpt) {
      cursor = this.writeKV(ws, cursor, 'Prompt (extracto)', payload.estimation.promptExcerpt);
    }
  }

  private writeSummaryHeader(ws: any, row: number, title: string): number {
    const cell = ws.getCell(row, 1);
    cell.value = title;
    cell.font = { bold: true, color: { argb: this.STYLE.sectionFont }, size: 11, name: 'Consolas' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.STYLE.sectionFill } };
    ws.mergeCells(row, 1, row, 2);
    ws.getRow(row).height = 20;
    return row + 1;
  }

  private writeKV(ws: any, row: number, label: string, value: unknown, numFmt?: string): number {
    const k = ws.getCell(row, 1);
    const v = ws.getCell(row, 2);
    k.value = label;
    v.value = value as any;
    k.font = { color: { argb: 'FF666666' }, size: 10 };
    v.font = { bold: true, size: 11 };
    if (numFmt) v.numFmt = numFmt;
    return row + 1;
  }

  private writeTwoColumnTable(
    ws: any,
    startRow: number,
    headers: [string, string],
    data: Array<[string, number]>,
    numFmt?: string
  ): number {
    // Cabecera de la mini-tabla
    const h1 = ws.getCell(startRow, 1);
    const h2 = ws.getCell(startRow, 2);
    h1.value = headers[0];
    h2.value = headers[1];
    [h1, h2].forEach(c => {
      c.font = { bold: true, size: 10, color: { argb: 'FF666666' } };
      c.border = { bottom: { style: 'thin', color: { argb: this.STYLE.borderSubtle } } };
    });

    // Filas
    let row = startRow + 1;
    for (const [k, val] of data) {
      ws.getCell(row, 1).value = k;
      const valCell = ws.getCell(row, 2);
      valCell.value = val;
      if (numFmt) valCell.numFmt = numFmt;
      row += 1;
    }
    return row;
  }

  private humanProvenance(p: ExportProvenance): string {
    switch (p) {
      case ExportProvenance.AI:                return 'IA';
      case ExportProvenance.AI_MODIFIED:       return 'IA (Modificada)';
      case ExportProvenance.HISTORIC:          return 'Histórica';
      case ExportProvenance.HISTORIC_MODIFIED: return 'Histórica (Modificada)';
      case ExportProvenance.MANUAL:            return 'Manual';
      case ExportProvenance.MANUAL_MODIFIED:   return 'Manual (Modificada)';
      default:                                 return 'Desconocido';
    }
  }

  // ==========================================================================
  // UTILS
  // ==========================================================================

  private buildFilename(payload: ExportPayload): string {
    const ts = this.timestampSlug(payload.estimation.exportedAt);
    const id = payload.estimation.id;
    return `estimacion-${id}-${ts}.xlsx`;
  }

  /** "2026-06-06T12:34:56.789Z" → "20260606-1234" (hora local). */
  private timestampSlug(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      '-' +
      pad(d.getHours()) +
      pad(d.getMinutes())
    );
  }
}

/**
 * Adapta un ExtraColumnSpec (del helper centralizado) al shape interno
 * que usa el XLSX exporter para columnas. Función a nivel módulo para
 * mantener la regla DRY — el helper no necesita conocer al exporter.
 */
function adaptExtraToXlsxColumn(spec: ExtraColumnSpec): {
  header: string;
  key: string;
  width: number;
  wrap?: boolean;
  numFmt?: string;
  pick: (t: ExportTaskRecord) => unknown;
  isNumeric?: boolean;
} {
  return {
    header: spec.header,
    key: spec.key,
    width: spec.width ?? 20,
    wrap: spec.wrap,
    numFmt: spec.isNumeric ? '0.00' : undefined,
    pick: spec.pick,
    isNumeric: spec.isNumeric
  };
}